import React from 'react'
import styled from 'styled-components'
import EventRegisterButton from 'components_v2/Events/EventRegisterButton'
import CommonLink from 'components_v2/CommonLink'
import { community_event_card_click_event } from 'gtm/pushEvents'
import { EventStatusBadge } from '../EventStatusBadge'
import {
  formatEventDate,
  formatEventTimeRange,
  isSameEventDay
} from 'components_v2/Events/formatEvent'
import { ExtendedDateComponent, LocationIcon } from '../EventDetailView.style'
import { useNavigate } from 'react-router-dom'
import { LINK_SOURCE } from 'components_v2/common'

const Card = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  /* border-radius: 12px; */
  overflow: hidden;
  cursor: pointer;
  background-image: url(${({ image }) => image});
  background-size: cover;
  background-position: center;
  color: #fff;

  @media (max-width: 768px) {
    aspect-ratio: 49 / 54;
    flex: 0 0 100%;
  }
  @media (min-width: 768px) and (max-width: 1199px) {
    flex: 0 0 calc((100% - 32px) / 2);
  }
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.8) 5%,
    rgba(0, 0, 0, 0.25) 40%,
    rgba(0, 0, 0, 0.05) 100%
  );
`

const Content = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 16px;
`

const Title = styled.h2`
  color: #fff;
  font-family: 'Proxima-Nova';
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.9px;
  text-transform: uppercase;
`

const InfoRow = styled.div`
  /* display: flex;
  flex-wrap: wrap; */
  margin-top: 12px;
  font-size: clamp(13px, 1.2vw, 15px);
  /* display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  grid-template-columns: 40% 60%; */
  gap: 6px;
`

const FirstColumn = styled.div`
  margin-bottom: 8px;
  .feature & {
    display: inline;
  }
`

const SecondColumn = styled.div`
  display: flex;
  gap: 10px;
`

const Info = styled.div`
  display: flex;
  color: #fff;
  font-family: 'Proxima-Nova';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  letter-spacing: 0.48px;
  gap: 8px;
  margin-right: 10px;
`

const TimeInfo = styled.div`
  display: flex;
  color: #fff;
  font-family: 'Proxima-Nova';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  letter-spacing: 0.48px;
  gap: 8px;
  margin-right: 8px;
  min-width: 147px;
  span {
    white-space: nowrap;
  }
`

const Actions = styled.div`
  display: flex;
  align-items: end;
  gap: 40px;
  margin-top: 22px;
  width: fit-content;
`

const Link = styled.div`
  color: #fff;
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.352px;
  text-decoration-line: underline;
`

const calenderIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
  >
    <path
      d='M5.2002 1.59961H10.7998V0H11.5996V1.59961H15.2002V15.2002H0.799805V1.59961H4.40039V0H5.2002V1.59961ZM1.59961 6V14.4004H14.4004V6H1.59961ZM1.59961 5.2002H14.4004V2.40039H11.5996V4H10.7998V2.40039H5.2002V4H4.40039V2.40039H1.59961V5.2002Z'
      fill='white'
    />
  </svg>
)

const timeIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
  >
    <path
      d='M8 0.799805C11.9764 0.799805 15.2002 4.02355 15.2002 8C15.2002 11.9764 11.9764 15.2002 8 15.2002C4.02355 15.2002 0.799805 11.9764 0.799805 8C0.799805 4.02355 4.02355 0.799805 8 0.799805ZM8 1.59961C4.46538 1.59961 1.59961 4.46538 1.59961 8C1.59961 11.5346 4.46538 14.4004 8 14.4004C11.5346 14.4004 14.4004 11.5346 14.4004 8C14.4004 4.46538 11.5346 1.59961 8 1.59961ZM8 7.43457L11.083 10.5176L10.5176 11.083L7.2002 7.76562V3.59961H8V7.43457Z'
      fill='white'
    />
  </svg>
)

const DateTimeContainer = ({
  startAt,
  endAt,
  venue,
  timezone,
  isFeatured = false
}) => {
  const isSameDay = isSameEventDay(startAt, endAt, timezone)

  return (
    <InfoRow className='infoRow'>
      {isSameDay && (
        <FirstColumn>
          {startAt && (
            <Info>
              <div>{calenderIcon}</div>
              <div> {formatEventDate(startAt, timezone)}</div>
            </Info>
          )}
        </FirstColumn>
      )}
      <SecondColumn className='secondInfoRow'>
        {startAt && (
          <TimeInfo
            {...(!isSameDay &&
              !isFeatured && {
                style: { alignItems: 'center', marginRight: '1rem' }
              })}
          >
            <div>{isSameDay ? timeIcon : calenderIcon}</div>
            <div>
              {isSameDay ? (
                <p>{formatEventTimeRange(startAt, endAt, timezone)}</p>
              ) : (
                <ExtendedDateComponent
                  startAt={startAt}
                  endAt={endAt}
                  timezone={timezone}
                  br={!isFeatured}
                />
              )}
            </div>
          </TimeInfo>
        )}
        {venue && (
          <Info
            {...(!isSameDay &&
              !isFeatured && { style: { alignItems: 'center' } })}
          >
            <div>
              <LocationIcon color='#fff' size={16} />
            </div>
            <div>{venue}</div>
          </Info>
        )}
      </SecondColumn>
    </InfoRow>
  )
}

const EventCard = (props = {}) => {
  //This component is used in two different places, where the props have different structures. That's why we're destructuring them differently.

  //When called from the Events Grid on the listing page.
  const { event, className, style, onRegistrationChange, location, position } =
    props

  //When called from the More Events carousel on the details page
  const {
    id,
    title,
    coverUrl,
    startAt,
    endAt,
    timezone,
    isPast,
    registrationStatus,
    registrationQuestions,
    registrationDefaults,
    venue,
    salesEnd,
    salesStart,
    spotsRemaining = null,
    usedSlots = 0
  } = event ?? props

  const maxSlots = spotsRemaining === null ? 0 : spotsRemaining + usedSlots
  const detailPath = id ? `/events/${id}` : '#'
  const viewDetailsHref = LINK_SOURCE
    ? `${detailPath}?source=${LINK_SOURCE}`
    : detailPath
  const handleActionClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleCardClick = () => {
    community_event_card_click_event({
      event: { id, title },
      location,
      position
    })
  }

  const isFeatured = String(className).includes('featured')
  const navigate = useNavigate()

  return (
    <Card
      as={CommonLink}
      to={detailPath}
      image={coverUrl}
      className={className}
      style={style}
      onClick={handleCardClick}
    >
      <Overlay />
      <EventStatusBadge
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          padding: '2px 8px'
        }}
        maxSlots={maxSlots}
        usedSlots={usedSlots}
        registrationStatus={registrationStatus}
        salesEnd={salesEnd}
        salesStart={salesStart}
      />
      <Content>
        <Title>{title}</Title>
        <DateTimeContainer
          startAt={startAt}
          endAt={endAt}
          venue={venue}
          timezone={timezone}
          isFeatured={isFeatured}
        />

        <Actions onClick={handleActionClick}>
          <EventRegisterButton
            {...event}
            eventId={event?.id ?? id}
            eventTitle={event?.title ?? title}
            registrationQuestions={
              event?.registrationQuestions ?? registrationQuestions
            }
            registrationDefaults={
              event?.registrationDefaults ?? registrationDefaults
            }
            registrationStatus={event?.registrationStatus ?? registrationStatus}
            isPast={isPast}
            showCancelButton={false}
            onRegistrationChange={onRegistrationChange}
          />
          <Link
            onClick={() => navigate(viewDetailsHref)}
            style={{
              color: '#fff',
              textDecoration: 'underline',
              whiteSpace: 'nowrap'
            }}
          >
            View Details
          </Link>
        </Actions>
      </Content>
    </Card>
  )
}

export default EventCard
